import React, { useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  TextInput,
  Image,
  Platform,
  ScrollView,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { colors, spacing, typography } from '../theme';
import { designTokens } from '../theme/design-system';
import { useAppStore } from '../store/appStore';
import { useThemedStyles } from '../hooks/useThemedStyles';
import { withAlpha } from '../theme';

export interface Note {
  _id?: string;
  content: string;
  createdBy: {
    _id?: string;
    id?: string;
    firstName?: string;
    lastName?: string;
    email?: string;
    avatar?: string;
    profilePicture?: string;
  };
  createdAt: Date | string;
  updatedAt?: Date | string;
}

export interface NotesSectionProps {
  notes: Note[];
  onAddNote: (content: string) => void;
  onDeleteNote?: (noteId: string | number) => void;
  placeholder?: string;
  title?: string;
  editable?: boolean;
  currentUserId?: string;
  /** Hides the built-in collapsible title row — for callers (e.g. a modal) that
   * already render their own header and would otherwise show two stacked titles. */
  showHeader?: boolean;
  /** Smaller avatars/gaps for narrow hosts (e.g. the ~350px right pane) —
   * the default sizing was tuned for the full-width Notes modal and left
   * very little room for note text in a narrow container. */
  compact?: boolean;
  previewCount?: number;
  maxListHeight?: number;
}

export const NotesSection: React.FC<NotesSectionProps> = ({
  notes = [],
  onAddNote,
  onDeleteNote,
  placeholder = 'Add a note...',
  title = 'Notes',
  editable = true,
  currentUserId,
  showHeader = true,
  compact = false,
  previewCount,
  maxListHeight,
}) => {
  const styles = useThemedStyles(getStyles);
  const { user, businessRole } = useAppStore();
  const [newNote, setNewNote] = useState('');
  const [isExpanded, setIsExpanded] = useState(true);
  const [showAllNotes, setShowAllNotes] = useState(false);
  const visibleNotes = previewCount && !showAllNotes ? notes.slice(0, previewCount) : notes;
  const hasMoreNotes = !!previewCount && notes.length > previewCount;

  const handleAddNote = () => {
    if (newNote.trim()) {
      onAddNote(newNote.trim());
      setNewNote('');
    }
  };

  const getInitials = (firstName?: string, lastName?: string, email?: string): string => {
    if (firstName && lastName) {
      return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
    }
    if (firstName) {
      return firstName.charAt(0).toUpperCase();
    }
    if (email) {
      return email.charAt(0).toUpperCase();
    }
    return 'U';
  };

  const getUserDisplayName = (noteUser: Note['createdBy']): string => {
    if (noteUser.firstName && noteUser.lastName) {
      return `${noteUser.firstName} ${noteUser.lastName}`;
    }
    if (noteUser.firstName) {
      return noteUser.firstName;
    }
    if (noteUser.email) {
      return noteUser.email.split('@')[0];
    }
    return 'Unknown User';
  };

  const formatDate = (date: Date | string): string => {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    const now = new Date();
    const diff = now.getTime() - dateObj.getTime();
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;
    
    return dateObj.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      year: dateObj.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
    });
  };

  const canDeleteNote = (note: Note): boolean => {
    if (!onDeleteNote) return false;
    
    // Check if user is admin or owner - they can delete any note
    const isAdminOrOwner = businessRole === 'admin' || businessRole === 'owner';
    if (isAdminOrOwner) {
      return true;
    }
    
    // Otherwise, only the note owner can delete their own note
    const noteUserId = note.createdBy._id || note.createdBy.id;
    const userId = currentUserId || user?.id;
    return noteUserId === userId;
  };

  return (
    <View style={styles.container}>
      {showHeader && (
        <TouchableOpacity
          style={styles.header}
          onPress={() => setIsExpanded(!isExpanded)}
          activeOpacity={0.7}
        >
          <Text style={styles.title}>{title}</Text>
          <View style={styles.headerRight}>
            {notes.length > 0 && (
              <Text style={styles.noteCount}>{notes.length}</Text>
            )}
            <Ionicons
              name={isExpanded ? 'chevron-up' : 'chevron-down'}
              size={20}
              color={colors.textSecondary}
            />
          </View>
        </TouchableOpacity>
      )}

      {(isExpanded || !showHeader) && (
        <>
          {/* Notes List */}
          {notes.length > 0 ? (
            <>
              <ScrollView
                style={maxListHeight ? [styles.notesScrollArea, { maxHeight: maxListHeight }] : undefined}
                contentContainerStyle={styles.notesList}
                nestedScrollEnabled
                showsVerticalScrollIndicator={showAllNotes && notes.length > (previewCount || 0)}
              >
              {visibleNotes.map((note, index) => {
                const noteUser = note.createdBy;
                const avatarUrl = noteUser.avatar || noteUser.profilePicture;
                const displayName = getUserDisplayName(noteUser);
                const initials = getInitials(noteUser.firstName, noteUser.lastName, noteUser.email);
                const canDelete = canDeleteNote(note);

                return (
                  <View key={note._id || index} style={[styles.noteItem, compact && styles.noteItemCompact]}>
                    <View style={[styles.noteAvatar, compact && styles.noteAvatarCompact]}>
                      {avatarUrl ? (
                        <Image
                          source={{ uri: avatarUrl }}
                          style={[styles.avatarImage, compact && styles.avatarImageCompact] as any}
                          onError={() => {}}
                        />
                      ) : (
                        <View style={[styles.avatarPlaceholder, { backgroundColor: withAlpha(colors.primary, 0.13) }]}>
                          <Text style={styles.avatarText}>{initials}</Text>
                        </View>
                      )}
                    </View>
                    <View style={[styles.noteContent, compact && styles.noteContentCompact]}>
                      <View style={styles.noteHeader}>
                        <Text style={styles.noteAuthor} numberOfLines={1}>{displayName}</Text>
                        <Text style={styles.noteDate}>{formatDate(note.createdAt)}</Text>
                        {canDelete && (
                          <TouchableOpacity
                            style={styles.deleteButton}
                            onPress={() => {
                              // Use _id if available, otherwise use index as fallback
                              const noteId = note._id || index;
                              onDeleteNote?.(noteId);
                            }}
                            activeOpacity={0.7}
                          >
                            <Ionicons name="close-circle" size={16} color={colors.error} />
                          </TouchableOpacity>
                        )}
                      </View>
                      <Text style={styles.noteText}>{note.content}</Text>
                    </View>
                  </View>
                );
              })}
              </ScrollView>
              {hasMoreNotes && (
                <TouchableOpacity
                  style={styles.showMoreButton}
                  onPress={() => setShowAllNotes((value) => !value)}
                  activeOpacity={0.75}
                >
                  <Text style={styles.showMoreText}>
                    {showAllNotes ? 'Show less' : `Show ${notes.length - previewCount} more`}
                  </Text>
                </TouchableOpacity>
              )}
            </>
          ) : (
            <View style={styles.emptyState}>
              <Ionicons name="document-text-outline" size={48} color={colors.textLight} />
              <Text style={styles.emptyText}>No notes yet</Text>
            </View>
          )}

          {/* Add Note Input */}
          {editable && (
            <View style={[styles.addNoteContainer, compact && styles.addNoteContainerCompact]}>
              <View style={[styles.currentUserAvatar, compact && styles.noteAvatarCompact]}>
                {user?.avatar || user?.profilePicture ? (
                  <Image
                    source={{ uri: user.avatar || user.profilePicture }}
                    style={[styles.avatarImage, compact && styles.avatarImageCompact] as any}
                    onError={() => {}}
                  />
                ) : (
                  <View style={[styles.avatarPlaceholder, { backgroundColor: withAlpha(colors.primary, 0.13) }]}>
                    <Text style={styles.avatarText}>
                      {getInitials(user?.firstName, user?.lastName, user?.email)}
                    </Text>
                  </View>
                )}
              </View>
              <View style={styles.inputContainer}>
                <TextInput
                  style={styles.noteInput}
                  value={newNote}
                  onChangeText={setNewNote}
                  placeholder={placeholder}
                  placeholderTextColor={colors.textLight}
                  multiline
                  maxLength={500}
                />
                <TouchableOpacity
                  style={[styles.sendButton, !newNote.trim() && styles.sendButtonDisabled]}
                  onPress={handleAddNote}
                  disabled={!newNote.trim()}
                  activeOpacity={0.7}
                >
                  <Ionicons
                    name="send"
                    size={20}
                    color={newNote.trim() ? colors.primary : colors.textLight}
                  />
                </TouchableOpacity>
              </View>
            </View>
          )}
        </>
      )}
    </View>
  );
};

const getStyles = () => StyleSheet.create({
  container: {
    marginBottom: spacing.md,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: spacing.md,
  },
  title: {
    fontSize: typography.fontSize.lg,
    fontWeight: typography.fontWeight.semibold as any,
    color: colors.textDark,
    fontFamily: typography.fontFamily.semibold,
  },
  headerRight: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
  },
  noteCount: {
    fontSize: typography.fontSize.sm,
    color: colors.textSecondary,
    fontFamily: typography.fontFamily.regular,
  },
  notesList: {
    gap: spacing.md,
    marginBottom: spacing.md,
  },
  notesScrollArea: {
    marginBottom: spacing.xs,
  },
  showMoreButton: {
    alignSelf: 'flex-start',
    paddingVertical: spacing.xs,
    marginBottom: spacing.sm,
  },
  showMoreText: {
    fontSize: typography.fontSize.sm,
    fontFamily: typography.fontFamily.medium,
    color: colors.primary,
  },
  noteItem: {
    flexDirection: 'row',
    gap: spacing.sm,
  },
  noteItemCompact: {
    gap: spacing.xs,
  },
  noteAvatar: {
    width: 40,
    height: 40,
    borderRadius: 20,
    overflow: 'hidden',
  },
  noteAvatarCompact: {
    width: 28,
    height: 28,
    borderRadius: 14,
  },
  avatarImage: {
    width: '100%',
    height: '100%',
    borderRadius: 20,
  },
  avatarImageCompact: {
    borderRadius: 14,
  },
  avatarPlaceholder: {
    width: '100%',
    height: '100%',
    borderRadius: 20,
    justifyContent: 'center',
    alignItems: 'center',
  },
  avatarText: {
    fontSize: typography.fontSize.sm,
    fontWeight: typography.fontWeight.bold as any,
    color: colors.primary,
    fontFamily: typography.fontFamily.bold,
  },
  noteContent: {
    flex: 1,
    padding: spacing.sm,
    backgroundColor: colors.lightGray,
    borderRadius: designTokens.borderRadius.md,
  },
  noteContentCompact: {
    padding: spacing.xs,
  },
  noteHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
    marginBottom: spacing.xs / 2,
  },
  noteAuthor: {
    fontSize: typography.fontSize.sm,
    fontWeight: typography.fontWeight.semibold as any,
    color: colors.textDark,
    fontFamily: typography.fontFamily.semibold,
  },
  noteDate: {
    fontSize: typography.fontSize.xs,
    color: colors.textSecondary,
    fontFamily: typography.fontFamily.regular,
  },
  deleteButton: {
    marginLeft: 'auto',
    padding: spacing.xs / 2,
  },
  noteText: {
    fontSize: typography.fontSize.base,
    color: colors.text.primary,
    fontFamily: typography.fontFamily.regular,
    lineHeight: 20,
  },
  emptyState: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: spacing.lg,
    marginBottom: spacing.sm,
  },
  emptyText: {
    fontSize: typography.fontSize.base,
    color: colors.textSecondary,
    marginTop: spacing.sm,
    fontFamily: typography.fontFamily.regular,
  },
  addNoteContainer: {
    flexDirection: 'row',
    gap: spacing.sm,
    alignItems: 'flex-start',
  },
  addNoteContainerCompact: {
    gap: spacing.xs,
  },
  currentUserAvatar: {
    width: 40,
    height: 40,
    borderRadius: 20,
    overflow: 'hidden',
  },
  inputContainer: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'flex-end',
    backgroundColor: colors.lightGray,
    borderRadius: designTokens.borderRadius.md,
    padding: spacing.xs,
    gap: spacing.xs,
  },
  noteInput: {
    flex: 1,
    fontSize: typography.fontSize.base,
    color: colors.text.primary,
    fontFamily: typography.fontFamily.regular,
    maxHeight: 100,
    paddingHorizontal: spacing.xs,
    paddingVertical: spacing.xs / 2,
  },
  sendButton: {
    padding: spacing.xs,
    justifyContent: 'center',
    alignItems: 'center',
  },
  sendButtonDisabled: {
    opacity: 0.5,
  },
});
